import { useMemo } from "react";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import taskStyles from "@components/drawers/TaskDrawerFields.module.scss";
import { useIsAccrualLedgerAvailableForTemplate } from "./useCloseTemplateAccrualLedgerAvailable";
import {
    REVIEW_FIELD_LABEL,
    ReviewField,
    SELECTABLE_REVIEW_FIELDS,
    readReviewFieldsFromViewConfig,
    withReviewFieldsAndColumns,
} from "./closeTemplateTaskViewConfig";
import { MultiSelectFilterCombobox } from "./MultiSelectFilterCombobox";

interface Props {
    viewConfig: Record<string, unknown> | null;
    onViewConfigChange: React.Dispatch<
        React.SetStateAction<Record<string, unknown> | null>
    >;
    basis?: CloseTemplateBasis | null;
}

export const ReviewFields = ({
    viewConfig,
    onViewConfigChange,
    basis,
}: Props) => {
    const selectedFields = readReviewFieldsFromViewConfig(viewConfig);
    const isAccrualLedgerAvailable =
        useIsAccrualLedgerAvailableForTemplate(basis);

    const availableFields = useMemo(() => {
        if (!isAccrualLedgerAvailable) {
            return SELECTABLE_REVIEW_FIELDS.filter(
                (f) => f !== "accrual_ledger",
            );
        }
        return SELECTABLE_REVIEW_FIELDS;
    }, [isAccrualLedgerAvailable]);

    const filteredSelectedFields = useMemo(() => {
        if (!isAccrualLedgerAvailable) {
            return selectedFields.filter((f) => f !== "accrual_ledger");
        }
        return selectedFields;
    }, [isAccrualLedgerAvailable, selectedFields]);

    return (
        <DescriptionListItem label="Fields to review" alignTop>
            <div className={taskStyles.detailSelectWrapper}>
                <MultiSelectFilterCombobox<ReviewField>
                    items={availableFields}
                    selectedValues={filteredSelectedFields}
                    onValuesChange={(next) => {
                        onViewConfigChange((prev) =>
                            withReviewFieldsAndColumns(prev, next),
                        );
                    }}
                    getLabel={(field) => REVIEW_FIELD_LABEL[field]}
                    placeholder="No fields"
                    searchPlaceholder="Search fields..."
                    emptyMessage="No fields found"
                    showClearButton={false}
                    triggerButtonProps={{
                        className: taskStyles.detailSelectTrigger,
                        block: false,
                    }}
                />
            </div>
        </DescriptionListItem>
    );
};
